import { z } from 'zod'
import * as http from '@/util/http'
import { isAfter } from 'date-fns'
import { type ProgressFlag, saveProgress } from '@/util/progress'
import { format, set } from 'date-fns'
import { queryClient } from '@/util/queryClient'
import { fetchProgressFlags } from '@/util/progress'

export const CourseSchema = z.object({
  id: z.number(),
  name: z.string(),
  isActive: z.boolean(),
})

export const ProgramSchema = z.object({
  id: z.number(),
  name: z.string(),
  programType: z.string(),
  durationYears: z.number(),
  isActive: z.boolean(),
  courses: z.array(CourseSchema).optional().nullable(),
})

export const SchoolSchema = z.object({
  id: z.number(),
  name: z.string(),
  institutionType: z.string(), // z.nativeEnum(Institutiontype)
  adminDivision: z.string(), // z.nativeEnum(Prefecture)
  subnationalUnit: z.string(), // z.nativeEnum(Region)
  programs: z.array(ProgramSchema).optional().nullable(),
})

export const EducationHistorySchema = z.object({
  id: z.number().int(),
  // profileId: z.number().int(),
  programType: z.string(),
  school: SchoolSchema.optional().nullable(),
  academicProgram: ProgramSchema.optional().nullable(),
  academicCourse: CourseSchema.optional().nullable(),
  enrollmentStartDate: z.preprocess((val) => {
    if (typeof val === 'string' || val instanceof Date) return new Date(val)
  }, z.date()),
  enrollmentEndDate: z.string().optional().nullable(),
  extraInfo: z.any().optional().nullable(),
})

export type EducationHistory = z.infer<typeof EducationHistorySchema>

export async function fetchEducationHistory(): Promise<EducationHistory[]> {
  const { data } = await http.get<EducationHistory[]>('/api/v1/user/profile/education-history')
  return EducationHistorySchema.array().parse(data)
}

// TODO: unify this
export function calGrade(enrollmentStartDate: Date | null): number {
  if (!enrollmentStartDate) return 0
  const now = new Date()
  const thisYear = now.getFullYear()
  const aprilFirst = new Date(thisYear, 3, 1)
  const enrollmentStartYear = enrollmentStartDate.getFullYear()
  return thisYear < enrollmentStartYear
    ? 0
    : thisYear - enrollmentStartYear + (isAfter(now, aprilFirst) ? 1 : 0)
}

export const DEFAULT_OPTION = -1

export const EduInfoSchema = z.object({
  programType: z.string().default(''),
  region: z.string().min(1, '地方を選択してください').default(''),
  prefecture: z.string().min(1, '都道府県を選択してください').default(''),
  schoolId: z.int({ error: '学校を選択してください' }).gt(0).default(DEFAULT_OPTION),
  academicProgramId: z.int().gte(0).default(DEFAULT_OPTION),
  academicCourseId: z.int().gte(0).default(DEFAULT_OPTION),
  grade: z.int().default(0),
})

export type EduInfoForm = z.input<typeof EduInfoSchema>

export const EduInfosSchema = z.object({
  edus: z.array(EduInfoSchema),
})
export type EduInfoFormList = z.infer<typeof EduInfosSchema>

export const DEFAULT_EDU_INFO: EduInfoForm = EduInfoSchema.parse({})

export type EduInfoForms = { edus: EduInfoForm[] }

function estimateEnrollmentStartDate(grade: number): Date {
  const now = new Date()
  return set(new Date(), {
    year: now.getFullYear() - grade + (now.getMonth() >= 3 ? 1 : 0),
    month: 3,
    date: 1,
  }) // month: 0-based，3 = 4月
}

// TODO: find a way to refactor these types
export type EducationHistoryUpdate = {
  programType: string
  schoolId: number
  academicProgramId?: number
  academicCourseId?: number
  grade: number
}

export async function syncEducationHistory(edus: EducationHistoryUpdate[]) {
  await http.put<number>('/api/v1/user/profile/education-history', {
    payload: edus.map((edu) => ({
      ...edu,
      academicProgramId: edu.academicProgramId || undefined,
      academicCourseId: edu.academicCourseId || undefined,
      enrollmentStartDate: format(estimateEnrollmentStartDate(edu.grade), 'yyyy-MM-dd'),
    })),
  })
}

export async function syncProgress(flags: ProgressFlag, syncDb: boolean = true) {
  const previousFlags = await queryClient.ensureQueryData<ProgressFlag>({
    queryKey: ['progress-flags'],
    queryFn: fetchProgressFlags,
    staleTime: Infinity,
  })

  try {
    if (syncDb) {
      await saveProgress(flags)
    }
    queryClient.setQueryData(['progress-flags'], () => previousFlags | flags)
  } catch (err) {
    console.log(err)
  }
}
